import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LinkSimple } from '@phosphor-icons/react';

// TUNEAMP_ADD_SONG_SOURCE_MODE_V1

function formatTaxonomyLabel(value) {
  if (!value) return '';

  const special = {
    'afro': 'Afro',
    'hip-hop': 'Hip-Hop',
    'rnb-soul': 'R&B / Soul',
    'reggae-dancehall': 'Reggae / Dancehall',
    'soundtrack-cinematic': 'Soundtrack / Cinematic',
    'drum-and-bass': 'Drum & Bass',
    'liquid-dnb': 'Liquid DnB',
    'atmospheric-dnb': 'Atmospheric DnB',
    'minimal-dnb': 'Minimal DnB',
    'dancefloor-dnb': 'Dancefloor DnB',
    'tech-house': 'Tech House',
    'afro-house': 'Afro House',
    'melodic-house': 'Melodic House',
    'melodic-techno': 'Melodic Techno',
    'melodic-dubstep': 'Melodic Dubstep',
    'deep-house': 'Deep House',
    'deep-dubstep': 'Deep Dubstep',
    'uk-garage': 'UK Garage',
    '2-step': '2-Step',
    'acid-house': 'Acid House',
    'acid-techno': 'Acid Techno',
    'idm': 'IDM',
    'j-pop': 'J-Pop',
    'k-pop': 'K-Pop',
    'rnb': 'R&B',
    'neo-soul': 'Neo-Soul',
    'alt-pop': 'Alt-Pop',
    'alt-rock': 'Alt-Rock',
  };

  if (special[value]) return special[value];

  return value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const emptyForm = {
  title: '',
  artist: '',
  source_url: '',
  genre: '',
  subgenre: '',
  microgenre: '',
  soundcloud_url: '',
  soundcloud_genre: '',
  soundcloud_embed_html: '',
  cover_url: '',
  spotify_url: '',
  youtube_url: '',
  lyrics: '',
};

export default function AddSongDialog({
  open,
  onOpenChange,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [link, setLink] = useState('');
  const [resolving, setResolving] = useState(false);
  const [saving, setSaving] = useState(false);

  const [taxonomy, setTaxonomy] = useState({
    genres: [],
    subgenres_by_genre: {},
    microgenres_by_genre: {},
  });

  useEffect(() => {
    if (!open) return;

    api.get('/labels/filters')
      .then(r => {
        setTaxonomy(r.data || {
          genres: [],
          subgenres_by_genre: {},
          microgenres_by_genre: {},
        });
      })
      .catch(() => {});
  }, [open]);

  const reset = () => {
    setForm(emptyForm);
    setLink('');
    setResolving(false);
    setSaving(false);
  };

  const handleOpenChange = value => {
    if (!value) reset();
    onOpenChange?.(value);
  };

  const resolveLink = async () => {
    const value = link.trim();

    if (!value) return;

    setResolving(true);

    setForm(prev => ({
      ...prev,
      source_url: value,
    }));

    try {
      const { data } = await api.post('/links/resolve', {
        url: value,
      });

      setForm(prev => ({
        ...prev,
        source_url: value,
        title: data.title || prev.title,
        artist: data.artist || prev.artist,

        genre: data.genre || prev.genre,
        subgenre: data.subgenre || '',
        microgenre: data.microgenre || '',

        soundcloud_url: data.soundcloud_url || '',
        soundcloud_genre: data.soundcloud_genre || '',
        soundcloud_embed_html: data.soundcloud_embed_html || '',

        spotify_url: data.spotify_url || '',
        youtube_url: data.youtube_url || '',
        cover_url: data.cover_url || '',
      }));

      toast.success(
        data.title
          ? `Found "${data.title}" by ${data.artist || 'Unknown artist'}`
          : 'Link read successfully'
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Couldn't read that link"
      );
    } finally {
      setResolving(false);
    }
  };

  const saveSong = async () => {
    if (!form.title.trim()) {
      toast.error('Title required');
      return;
    }

    if (!form.artist.trim()) {
      toast.error('Artist required');
      return;
    }

    if (!form.genre) {
      toast.error('Choose a genre');
      return;
    }

    if (!form.source_url.trim()) {
      toast.error('Add a link to the song');
      return;
    }

    setSaving(true);

    try {
      const { data } = await api.post('/songs', {
        ...form,
        source_url: form.source_url.trim(),
        title: form.title.trim(),
        artist: form.artist.trim(),
        subgenre: form.subgenre || null,
        microgenre: form.microgenre || null,
        soundcloud_url: form.soundcloud_url || null,
        soundcloud_genre: form.soundcloud_genre || null,
        spotify_url: form.spotify_url || null,
        youtube_url: form.youtube_url || null,
        cover_url: form.cover_url || null,
        lyrics: form.lyrics || null,
      });

      const savedSong = data;

      toast.success('Song added');

      onSaved?.(savedSong);

      reset();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not add song'
      );
    } finally {
      setSaving(false);
    }
  };

  const subgenres =
    taxonomy.subgenres_by_genre?.[form.genre] || [];

  const microgenres =
    taxonomy.microgenres_by_genre?.[form.genre]?.[form.subgenre] || [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden pr-3">
        <DialogHeader>
          <DialogTitle className="font-display">
            Add a song
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <LinkSimple
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                />

                <Input
                  data-testid="link-fetch-input"
                  placeholder="Paste any public track link…"
                  value={link}
                  onChange={e => {
                    const value = e.target.value;
                    setLink(value);
                    setForm(prev => ({
                      ...prev,
                      source_url: value,
                    }));
                  }}
                  className="pl-8 bg-zinc-950 border-zinc-800"
                />
              </div>

              <Button
                data-testid="link-fetch-btn"
                onClick={resolveLink}
                disabled={resolving || !link.trim()}
                variant="outline"
                className="border-zinc-800 hover:border-[#ccff00]/60 shrink-0"
              >
                {resolving ? 'Reading…' : 'Read link'}
              </Button>
            </div>
          </div>

          <Input
            data-testid="song-title-input"
            placeholder="Title"
            value={form.title}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />

          <Input
            data-testid="song-artist-input"
            placeholder="Artist"
            value={form.artist}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                artist: e.target.value,
              }))
            }
          />

          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              Genre
            </label>

            {!form.genre && (
              <div className="mt-2 mb-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5">
                <div className="text-xs text-zinc-300">
                  We couldn't detect the genre automatically.
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Please choose the closest genre for this track.
                </div>
              </div>
            )}

            <Select
              value={form.genre || undefined}
              onValueChange={value =>
                setForm(prev => ({
                  ...prev,
                  genre: value,
                  subgenre: '',
                  microgenre: '',
                }))
              }
            >
              <SelectTrigger
                data-testid="song-genre-select"
                className="mt-2 bg-zinc-950 border-zinc-800"
              >
                <SelectValue placeholder="Select genre" />
              </SelectTrigger>

              <SelectContent>
                {taxonomy.genres.map(g => (
                  <SelectItem key={g} value={g}>
                    {formatTaxonomyLabel(g)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              Subgenre <span className="text-zinc-700">(optional)</span>
            </label>

            <Select
              value={form.subgenre || undefined}
              onValueChange={value =>
                setForm(prev => ({
                  ...prev,
                  subgenre: value,
                  microgenre: '',
                }))
              }
              disabled={!form.genre || !subgenres.length}
            >
              <SelectTrigger
                data-testid="song-subgenre-select"
                className="mt-2 bg-zinc-950 border-zinc-800"
              >
                <SelectValue
                  placeholder={
                    form.genre
                      ? 'Select subgenre'
                      : 'Select genre first'
                  }
                />
              </SelectTrigger>

              <SelectContent>
                {subgenres.map(sg => (
                  <SelectItem key={sg} value={sg}>
                    {formatTaxonomyLabel(sg)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              Microgenre <span className="text-zinc-700">(optional)</span>
            </label>

            <Select
              value={form.microgenre || undefined}
              onValueChange={value =>
                setForm(prev => ({
                  ...prev,
                  microgenre: value,
                }))
              }
              disabled={!form.subgenre || !microgenres.length}
            >
              <SelectTrigger
                data-testid="song-microgenre-select"
                className="mt-2 bg-zinc-950 border-zinc-800"
              >
                <SelectValue
                  placeholder={
                    form.subgenre
                      ? 'Select microgenre'
                      : 'Select subgenre first'
                  }
                />
              </SelectTrigger>

              <SelectContent>
                {microgenres.map(mg => (
                  <SelectItem key={mg} value={mg}>
                    {formatTaxonomyLabel(mg)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Textarea
            placeholder="Lyrics or key line (optional — helps AI capture the vibe)"
            value={form.lyrics}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                lyrics: e.target.value,
              }))
            }
            className="min-h-[90px]"
          />
        </div>

        <DialogFooter>
          <Button
            data-testid="song-save-btn"
            onClick={saveSong}
            disabled={saving}
            className="btn-neon rounded-full"
          >
            {saving ? 'Saving…' : 'Save song'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
